/**
 * Type utility that removes all dashes from a string literal type
 * @example
 * type Result = RemoveDash<'hello-world'> // type Result = 'helloworld'
 */
type RemoveDash<T extends string> = T extends `${infer First}-${infer Rest}`
  ? `${First}${RemoveDash<Rest>}`
  : T

/**
 * Removes all dash characters (-) from a string while preserving the literal type structure
 * @template T - The input string literal type
 * @param {T} value - The input string to process
 * @returns {RemoveDash<T>} A new string with all dashes removed, with precise typing
 * @throws {TypeError} When the input is not a string
 * @example
 * const result = removeDash('hello-world'); // Type: 'helloworld'
 * const typed = removeDash<'user-name'>('user-name'); // Type: 'username'
 * const template = removeDash('prefix-123'); // Type: 'prefix123'
 */
const removeDash = <T extends string>(value: T): RemoveDash<T> => {
  if (typeof value !== 'string') {
    throw new TypeError(`removeDash requires string as an argument. Got: ${typeof value}`)
  }

  return value.replace(/-/g, '') as RemoveDash<T>
}

export default removeDash
